import React from "react";
import { DateUtils } from "react-day-picker";
import uuid from "uuid";
import moment from "moment";

export interface ListItem {
  id: number;
  title: string;
  selected: boolean;
  key: string;
  value?: any;
}

export interface SelectItem {
  title: string;
  allowMultiple: boolean;
  isFilterActive: boolean;
  options: ListItem[];
}

const isSomeItemSelected = (options: ListItem[]) =>
  options.some((item: ListItem) => item.selected);

const useFilters = (userProps) => {
  const [state, setFiltersState] = React.useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {}
  );
  const [numOfActiveFilters, setNumOfActiveFilters] = React.useState(0);
  const [initialState, setInitialState] = React.useState(undefined);
  const isInitialMount = React.useRef(true);

  React.useEffect(() => {
    setFiltersState(userProps.initialState);
  }, [...userProps.dep]);

  React.useEffect(() => {
    if (isInitialMount.current) isInitialMount.current = false;
    else {
      setInitialState(userProps.initialState);
    }
  }, [...userProps.dep, isInitialMount.current]);

  const toggleSelected = (id: number, key: string) => {
    const { allowMultiple, options } = state[key];
    let newOptions = [...options];
    if (!allowMultiple) {
      newOptions = newOptions.map((item: ListItem) => {
        if (item.id !== id) {
          return { ...item, selected: false };
        }
        return item;
      });
    }
    // toggle selected dropdown item
    newOptions = newOptions.map((item) => {
      if (item.id === id) {
        return { ...item, selected: !item.selected };
      }
      return item;
    });
    // check if all items selected value is 'false'
    let isAllUnselected = true;
    newOptions.forEach((item: ListItem) => {
      if (item.selected) isAllUnselected = false;
    });
    if (isAllUnselected) {
      newOptions = initialState[key].options;
    }
    const isFilterActive = isSomeItemSelected(newOptions);
    setFiltersState({
      [key]: { ...state[key], options: newOptions, isFilterActive },
    });
  };

  // removed selected itmes for one list
  const removeAllSelected = (key: string) => {
    const options = [...initialState[key].options];
    const isFilterActive = isSomeItemSelected(options);
    setFiltersState({ [key]: { ...state[key], options, isFilterActive } });
  };

  const clearAllFilters = () => {
    const newState = { ...initialState };
    setFiltersState(newState);
  };

  const handleDayClick = (day: Date, modifiers) => {
    let date_object,
      keyFound = false;
    // search for object in state with type "date"
    for (const key in state) {
      if (state[key].type === "date") {
        date_object = state[key];
        keyFound = true;
      }
    }

    if (!keyFound) {
      return;
    }

    const { options, key } = date_object;
    const newOptions = [...options];
    if (modifiers.disabled) {
      return;
    }
    if (modifiers.selected) {
      const selectedIndex = newOptions?.findIndex(({ title }) =>
        DateUtils.isSameDay(new Date(title), day)
      );
      newOptions.splice(selectedIndex, 1);
    } else {
      newOptions.push({
        id: uuid(),
        title: day.toDateString(),
        selected: true,
        key,
        value: moment(day).format("YYYY-MM-DD"),
      });
    }
    const isFilterActive = isSomeItemSelected(newOptions);
    setFiltersState({
      [key]: { ...state[key], options: newOptions, isFilterActive },
    });
  };

  const handleCheckboxesChange = (values) => {
    let object_key,
      keyFound = false;

    for (const key in state) {
      if (state[key].type === "checkbox") {
        object_key = key;
        keyFound = true;
      }
    }
    if (!keyFound) {
      return;
    }
    setFiltersState({
      [object_key]: { ...state[object_key], selectedOptions: [...values] },
    });
  };

  return {
    state,
    setFiltersState,
    toggleSelected,
    removeAllSelected,
    clearAllFilters,
    handleDayClick,
    setNumOfActiveFilters,
    numOfActiveFilters,
    handleCheckboxesChange,
  } as const;
};

export default useFilters;
